import type { JSX } from 'react';
import { Gauge, ShieldCheck, Brain, Settings2, Server, BarChart3 } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { useContent } from '@data';
import { useT } from '@/i18n';
import { SectionHeader, RevealWrapper, ExpandCard } from '@components/ui';
import './ExpertiseTerrain.css';

type IconComp = React.ComponentType<LucideProps>;

const ICON_MAP = { Gauge, ShieldCheck, Brain, Settings2, Server, BarChart3 } satisfies Record<string, IconComp>;

function isKnownIcon(name: string): name is keyof typeof ICON_MAP {
  return name in ICON_MAP;
}

function ItemContent({ text }: { text: string }): JSX.Element {
  const dashIdx = text.indexOf(' — ');
  if (dashIdx !== -1) {
    return (
      <>
        <span className="ExpertiseCard__item-key">{text.slice(0, dashIdx)}</span>
        <span className="ExpertiseCard__item-detail"> — {text.slice(dashIdx + 3)}</span>
      </>
    );
  }
  return <span className="ExpertiseCard__item-key">{text}</span>;
}

export default function ExpertiseTerrain(): JSX.Element {
  const { EXPERTISE_CARDS } = useContent();
  const t = useT();

  return (
    <section id="expertise" className="section ExpertiseTerrain">
      <SectionHeader
        chip={t.sections.expertise.chip}
        chipVariant="p"
        title={t.sections.expertise.title}
        subtitle={t.sections.expertise.subtitle}
      />

      <div className="ExpertiseTerrain__grid">
        {EXPERTISE_CARDS.map((card, i) => {
          const Icon = isKnownIcon(card.icon) ? ICON_MAP[card.icon] : null;
          return (
            <RevealWrapper key={card.id} delay={(i % 4) + 1}>
              <ExpandCard
                cardClassName={`ExpertiseCard ExpertiseCard--${card.accentClass}`}
                ariaLabelledBy={`expertise-${String(i)}`}
                summary={
                  <>
                    <div className="ExpertiseCard__header">
                      <span className="ExpertiseCard__icon" aria-hidden="true">
                        {Icon !== null && <Icon size={22} strokeWidth={1.5} />}
                      </span>
                      <span className="ExpertiseCard__number" aria-hidden="true">
                        {card.number}
                      </span>
                    </div>
                    <h3 className="ExpertiseCard__title" id={`expertise-${String(i)}`}>
                      {card.title}
                    </h3>
                  </>
                }
                detail={
                  <>
                    <div className="ExpertiseCard__header">
                      <span className="ExpertiseCard__icon" aria-hidden="true">
                        {Icon !== null && <Icon size={22} strokeWidth={1.5} />}
                      </span>
                      <span className="ExpertiseCard__number" aria-hidden="true">
                        {card.number}
                      </span>
                    </div>
                    <h3 className="ExpertiseCard__title">{card.title}</h3>
                    <ul className="ExpertiseCard__items" role="list">
                      {card.items.map(item => (
                        <li key={item} className="ExpertiseCard__item">
                          <ItemContent text={item} />
                        </li>
                      ))}
                    </ul>
                    <p className="ExpertiseCard__footer">{card.footer}</p>
                  </>
                }
              />
            </RevealWrapper>
          );
        })}
      </div>
    </section>
  );
}
